//! Pure formatting and grouping helpers for the chat view.
//!
//! Extracted from Chat.tsx so the message-grouping logic — the interesting part
//! — can be unit-tested without mounting the 3,500-line component. No React, no
//! app state: just data in, data out.

/** The gap after which a new message starts a fresh author run rather than
 *  joining the previous one. */
export const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** A message as far as grouping cares: it needs an author, a name, and a
 *  timestamp. Kept structural so callers can pass their richer message type. */
export interface GroupableMessage {
  authorId: string;
  authorName: string;
  createdAt: string;
  /** "system" lines ("Asha changed the topic") never join a person's run. */
  kind?: "message" | "system";
}

export interface AuthorRun<M extends GroupableMessage> {
  authorId: string;
  authorName: string;
  /** A run of exactly one app-written line, rendered as a quiet notice. */
  system?: boolean;
  messages: M[];
}

export interface DayGroup<M extends GroupableMessage> {
  key: string;
  label: string;
  runs: AuthorRun<M>[];
}

/** A client-side id for an optimistic message before the server assigns one. */
export function newClientMessageId(): string {
  return typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `cid-${Date.now()}-${Math.random()}`;
}

export function formatClock(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function formatDaySeparator(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const today = now;
  const yesterday = new Date(now);
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  if (isSameDay(date, today)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

/**
 * Groups a flat, oldest-first message list into day sections, each with runs of
 * consecutive same-author messages collapsed (avatar/name shown once). A run
 * breaks when the author changes or more than five minutes passes.
 */
export function groupMessages<M extends GroupableMessage>(list: M[]): DayGroup<M>[] {
  const days: DayGroup<M>[] = [];

  for (const message of list) {
    const dayKey = message.createdAt.slice(0, 10);
    let day = days[days.length - 1];
    if (!day || day.key !== dayKey) {
      day = { key: dayKey, label: formatDaySeparator(message.createdAt), runs: [] };
      days.push(day);
    }

    const lastRun = day.runs[day.runs.length - 1];
    const lastMessage = lastRun?.messages[lastRun.messages.length - 1];
    const withinWindow =
      lastMessage &&
      new Date(message.createdAt).getTime() - new Date(lastMessage.createdAt).getTime() <= FIVE_MINUTES_MS;

    if (message.kind === "system") {
      day.runs.push({ authorId: message.authorId, authorName: message.authorName, system: true, messages: [message] });
    } else if (lastRun && !lastRun.system && lastRun.authorId === message.authorId && withinWindow) {
      lastRun.messages.push(message);
    } else {
      day.runs.push({ authorId: message.authorId, authorName: message.authorName, messages: [message] });
    }
  }

  return days;
}
